import { makeAutoObservable } from "mobx";
import { CacheAreaKey } from "../../config/cache-config";
import { StorageHelper } from "../../services/storage";
import { MakePaymentPayloadDTO } from "./interfaces/make-payment-payload.dto";
import { PaymentService } from "./interfaces/payment-service";
import { RequestRapydCheckout } from "./interfaces/request-rapyd-checkout";
import { PaymentStatus } from "./payment.store";
import { PaymentStatusPSH } from "./payment-request-psh";
import { MCTransactionTypes } from "./enums/mc-transaction-types";

export const ProcessingLimits = {
  MaxTime: 3600000,
  MaxErrors: 10,
  PollTimeStart: 10000
}

export const PollingDefaults = {
  pollCount: 0,
  duration: 10000,
  errorCount: 0
}

export class PaymentPollingStore {
  pollingStats = PollingDefaults;
  pendingPaymentPoll!: NodeJS.Timer;
  status = PaymentStatus.None;
  type!: MCTransactionTypes;
  message!: string | null;
  actions = {
    onPending: () => {
      this.status = PaymentStatus.Pending;
    },
    onSuccess: () => {
      sessionStorage.removeItem('dstv-ss.tid');
      this.status = PaymentStatus.Success;
      StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Account)
      StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Products)
      this.clearPoll();
    },
    onFailed: (message: string) => {
      sessionStorage.removeItem('dstv-ss.tid');
      this.message = message;
      this.status = PaymentStatus.Failure;
      this.clearPoll();
    },
  }
  countryName!: string;

  private transaction!: {
    id: string;
  } | null;

  constructor(private paymentService: PaymentService<MakePaymentPayloadDTO | RequestRapydCheckout>, countryName: string) {
    makeAutoObservable(this);
    this.changeCountryName(countryName);
  }

  changeCountryName(countryName: string) {
    this.countryName = countryName;
  }

  startPoll(transactionId: string, type: string) {
    if (!this.paymentService.canStatusPoll) {
      this.clearPoll();
      this.actions.onSuccess();
      return;
    }

    if (this.pendingPaymentPoll) {
      this.clearPoll();
    }

    this.type = +(type);
    this.status = PaymentStatus.Pending;
    this.message = null;
    this.transaction = { id: transactionId };
    this.pendingPaymentPoll = setInterval(() => {
      this.pollingStats.pollCount += this.pollingStats.pollCount;
      this.checkStatus();
    }, ProcessingLimits.PollTimeStart);
  }

  clearPoll() {
    this.pollingStats = PollingDefaults;
    this.transaction = null;
    if (this.pendingPaymentPoll) {
      clearInterval(this.pendingPaymentPoll);
    }
  }

  clearState() {
    this.clearPoll();
    this.status = PaymentStatus.None;
    this.message = null;
  }

  paymentFailed(reason: string) {
    this.actions.onFailed(reason);
  }

  private checkStatus() {
    if (!this.transaction) {
      console.error('Transaction lost')
      this.actions.onFailed('Transaction lost');
      return;
    }
    this.paymentService.PaymentStatus(this.countryName, this.transaction.id)
      .then(
        (statusResponse: any) => {
          this.pollingStats.errorCount = 0;

          if (
            statusResponse.status === 'Pending'
            || statusResponse.status === PaymentStatusPSH.ACT
            || statusResponse.status === PaymentStatusPSH.NEW
          ) {
            this.actions.onPending();
            return;
          }
          if (
            statusResponse.status === 'SUCCESS'
            || statusResponse.status === PaymentStatusPSH.DON
            || statusResponse.status === PaymentStatusPSH.CLO
          ) {
            this.actions.onSuccess();
            return;
          }
          if (
            statusResponse.status === 'FAILURE'
            || PaymentStatusPSH.EXP
            || PaymentStatusPSH.ERR
            || PaymentStatusPSH.REV
            || PaymentStatusPSH.CAN
          ) {
            this.actions.onFailed('Status Returned Failed');
            return;
          }
          this.handlePollingError();
        },
        (error) => {
          console.error('Error Checking Status -> ', error);
          this.handlePollingError();
        }
      );
  }

  private handlePollingError() {
    this.pollingStats.errorCount += 1;
    if (this.pollingStats.errorCount >= ProcessingLimits.MaxErrors) {
      this.actions.onFailed('Max error count reached on poll');
    }
  }
}